import { saveAs } from 'file-saver';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useMsal } from '@azure/msal-react';
import { notification } from 'antd';

import { Order, OrderSap } from '../../api-client';
import { RootState } from '../../application/store';
import { AdminApiClient, OrderApiClient } from '../../providers/api-provider';
import { upsertOrder } from '../../slices/OrderSummary/orderSummarySlice';
import { ORDER_STATUS } from '../../utils/constants/order-status';
import { STORAGE_KEYS } from '../../utils/constants/storage-keys';
import { sessionStorageHelper } from '../../utils/storage-helpers/session-storage-helper';

export const useOrder = () => {
	const { instance } = useMsal();

	const bvCustomer = useSelector((state: RootState) => state.customer);

	const dispatch = useDispatch();

	const autosaveOrderForCurrentCustomer = () =>
		// Auto save on every order change
		handleUpsertOrder(
			sessionStorageHelper.getStorageItem(STORAGE_KEYS.BV_ORDER_SUMMARY),
			(orderId) => handleRetrieveOrder(orderId),
			false,
		);

	const resetOrderForCurrentCustomer = () => {
		dispatch(
			upsertOrder({
				id: undefined,
				status: ORDER_STATUS.DRAFT,
				amount: 0,
				createdAt: new Date().toISOString(),
				customer: {
					id: bvCustomer.id,
					sapCode: bvCustomer.sapCode,
					salesGroupCode: bvCustomer.salesGroupCode,
					fullName: bvCustomer.fullName,
				},
				loginNameSalesAgent: undefined,
				note: '',
				poNumber: '',
				reason: undefined,
				products: [],
			}),
		);
	};

	// CRUD Operations

	const [retrieveOrderLoading, setRetrieveOrderLoading] =
		useState<boolean>(false);

	const handleRetrieveOrder = async (
		id: number = -1,
		callback?: () => void,
	) => {
		setRetrieveOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.getOrder(id)
			.then((response) => {
				dispatch(upsertOrder(response.data));

				callback && callback();
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to retrieve order!',
					description: `Unable to retrieve the order #${id}.`,
					duration: 2,
				});
			})
			.finally(() => {
				setRetrieveOrderLoading(false);
			});
	};

	const [retrieveOrderSap, setRetrieveOrderSap] = useState<OrderSap[]>([]);
	const [retrieveOrderSapLoading, setRetrieveOrderSapLoading] =
		useState<boolean>(false);

	const handleRetrieveOrderSap = async (id: number) => {
		setRetrieveOrderSapLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.getOrderSap(id)
			.then((response) => {
				setRetrieveOrderSap(response.data);
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to retrieve order sap!',
					description: `Unable to retrieve the order sap for the order #${id}.`,
					duration: 2,
				});
			})
			.finally(() => {
				setRetrieveOrderSapLoading(false);
			});
	};

	const [upsertOrderLoading, setUpsertOrderLoading] = useState<boolean>(false);

	const handleUpsertOrder = async (
		order: Order,
		callback?: (orderId: number) => void,
		showNotification = true,
	) => {
		setUpsertOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.upsertOrderDraft(order)
			.then((response) => {
				callback && callback(response.data);

				showNotification &&
					notification.success({
						message: 'Order saved!',
						description: 'Order successfully saved.',
						duration: 2,
					});
			})
			.catch((reason) => {
				showNotification &&
					notification.error({
						message: 'Unable to save order!',
						description: `Unable to save the order.`,
						duration: 2,
					});
			})
			.finally(() => {
				setUpsertOrderLoading(false);
			});
	};

	const [finalizeOrderLoading, setFinalizeOrderLoading] =
		useState<boolean>(false);

	const handleFinalizeOrder = async (
		orderId: number,
		onSuccess: () => void,
		onError: () => void,
	) => {
		setFinalizeOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.finalizeOrder(orderId)
			.then((response) => {
				resetOrderForCurrentCustomer();

				onSuccess();

				notification.success({
					message: 'Order finalized!',
					description: 'Order successfully finalized.',
					duration: 2,
				});
			})
			.catch((reason) => {
				if (reason.response && reason.response.status === 412) {
					dispatch(upsertOrder(reason.response.data));

					onError();

					notification.warning({
						message: 'Unable to finalize order!',
						description: `Unable to finalize the order, cause some products are unavailable. Current order is now successfully updated and ready to be completed.`,
						duration: 2,
					});
				} else {
					notification.error({
						message: 'Unable to finalize order!',
						description: `Unable to finalize the order.`,
						duration: 2,
					});
				}
			})
			.finally(() => {
				setFinalizeOrderLoading(false);
			});
	};

	const [deleteOrderLoading, setDeleteOrderLoading] = useState<boolean>(false);

	const handleDeleteOrder = async (id: number = -1, callback?: () => void) => {
		setDeleteOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.deleteOrder(id)
			.then(() => {
				resetOrderForCurrentCustomer();

				callback && callback();

				notification.success({
					message: 'Order deleted!',
					description: `Successfully delete the order #${id}.`,
					duration: 2,
				});
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to delete order!',
					description: `Unable to delete the order #${id}.`,
					duration: 2,
				});
			})
			.finally(() => {
				setDeleteOrderLoading(false);
			});
	};

	const [exportOrderLoading, setExportOrderLoading] = useState<boolean>(false);

	const handleExportOrder = async (customerName: string, id: number = -1) => {
		setExportOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.printOrder(id, { responseType: 'blob' })
			.then((response) => {
				saveAs(
					new Blob([response.data]),
					`${customerName.replace(/\s+/g, '')}-${new Date().toISOString().split('T')[0].replace(/-+/g, '')}-ORDER-${id}.pdf`,
				);
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to export order!',
					description: `Unable to export the order #${id}.`,
					duration: 2,
				});
			})
			.finally(() => {
				setExportOrderLoading(false);
			});
	};

	const [repriceOrderLoading, setRepriceOrderLoading] =
		useState<boolean>(false);

	const handleRepriceOrder = async (id: number = -1, callback?: () => void) => {
		setRepriceOrderLoading(true);

		const clientApi = await OrderApiClient(instance);

		clientApi
			.repriceOrder(id)
			.then((response) => {
				const isComplete = response.data && response.data.complete;
				const isPriceChanged = response.data && response.data.pricesIsChanged;
				const isPriceMissing = response.data && response.data.pricesMissing;

				if (isComplete) {
					handleRetrieveOrder(id);
					callback && callback();

					if (isPriceChanged) {
						if (isPriceMissing) {
							notification.warning({
								message: 'Warning - only some items price was update',
								description: `Partially reprice the order #${id}.`,
								duration: 2,
							});
						} else {
							notification.success({
								message: 'Repricing done successfully',
								description: `Successfully reprice the order #${id}.`,
								duration: 2,
							});
						}
					} else {
						notification.warning({
							message:
								'Warning - items price wasnt change' +
								(isPriceMissing ? ' and price still missing' : ''),
							description: `Reprice the order #${id} was done without variation.`,
							duration: 2,
						});
					}
				} else {
					notification.error({
						message: 'Unable to reprice order!',
						description: `Unable to reprice the order #${id}.`,
						duration: 2,
					});
				}
			})
			.catch((reason) => {
				notification.error({
					message: 'Unable to reprice order!',
					description: `Unable to reprice the order #${id}.`,
					duration: 2,
				});
			})
			.finally(() => {
				setRepriceOrderLoading(false);
			});
	};

	// =========================
	// Admin area
	// =========================

	const [adminOrders, setAdminOrders] = useState<any[] | undefined>(undefined);
	const [adminOrdersLoading, setAdminOrdersLoading] = useState<boolean>(false);

	const handleAdminOrdersByReasonCode = async (reasonCode: string) => {
		setAdminOrdersLoading(true);

		try {
			const adminApi = await AdminApiClient(instance);
			const response = await adminApi.getOrdersByReasonCode(reasonCode);
			setAdminOrders(response.data);
		} catch (err) {
			setAdminOrders([]);

			notification.error({
				message: 'Unable to retrieve products!',
				description: 'Unable to retrieve products.',
				duration: 2,
			});
		} finally {
			setAdminOrdersLoading(false);
		}
	};

	const [exportAdminOrdersLoading, setExportAdminOrdersLoading] =
		useState<boolean>(false);

	const handleExportAdminOrdersByReasonCode = async (reasonCode: string) => {
		setExportAdminOrdersLoading(true);

		try {
			const adminApi = await AdminApiClient(instance);

			const response = await adminApi.exportOrdersByReasonCode(reasonCode, {
				responseType: 'blob',
			});

			const cd =
				response.headers?.['content-disposition'] ||
				response.headers?.['Content-Disposition'];

			let filename = `orders_export_${reasonCode}_${
				new Date().toISOString().split('T')[0]
			}.xlsx`;

			const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(
				cd || '',
			);
			if (match) {
				filename = decodeURIComponent(match[1] || match[2]);
			}

			saveAs(new Blob([response.data]), filename);
		} catch (err) {
			notification.error({
				message: 'Unable to export orders!',
				description: 'Unable to export orders.',
				duration: 2,
			});
		} finally {
			setExportAdminOrdersLoading(false);
		}
	};

	return {
		autosaveOrderForCurrentCustomer,
		resetOrderForCurrentCustomer,
		// CRUD Operations
		handleRetrieveOrder,
		retrieveOrderLoading,
		handleRetrieveOrderSap,
		retrieveOrderSap,
		retrieveOrderSapLoading,
		handleUpsertOrder,
		upsertOrderLoading,
		handleFinalizeOrder,
		finalizeOrderLoading,
		handleDeleteOrder,
		deleteOrderLoading,
		handleExportOrder,
		exportOrderLoading,
		handleRepriceOrder,
		repriceOrderLoading,

		adminOrders,
		adminOrdersLoading,
		handleAdminOrdersByReasonCode,
		exportAdminOrdersLoading,
		handleExportAdminOrdersByReasonCode,
	};
};
